import { richTextTypography, useBorder } from "@pulse/shared-components";
import {
  IconButton,
  RichTextEditorProps,
  RichTextToolbarButtons,
  useCornerRadius,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { RichTextEditor } from "@surya-digital/leo-reactjs-remirror";
import { Instance } from "mobx-state-tree";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RTEFileServiceModel } from "../../../projects/models/RTEFileServiceModel";
import { observer } from "mobx-react";
import "./EmailTemplateContentRTE.css";
import { decodeSavedHtml } from "../../../utils/decodeSavedHtml";
import { Edit, Edit2, Eye } from "lucide-react";
import { Stack, Tooltip, Typography } from "@mui/material";

export const MESSAGE_FIELD_HEIGHT = "800px";
const CUSTOM_LINK_ENABLED_CLASS = "email-content-rte-popup-z-index-fix-enabled";

type EmailTemplateContentRTEProps = Pick<
  RichTextEditorProps,
  "initialValue" | "mode" | "onChange" | "error" | "helperText"
> & {
  rteFileService: Instance<typeof RTEFileServiceModel>;
  enableRTEPopupZIndexFix?: boolean;
  hidePreviewEditToggler?: boolean;
};

export const EmailTemplateContentRTE: React.FC<EmailTemplateContentRTEProps> =
  observer((props) => {
    const {
      rteFileService,
      enableRTEPopupZIndexFix = false,
      hidePreviewEditToggler = false,
      initialValue,
      mode: propsMode,
      onChange,
      helperText,
      error,
      ...richTextEditorProps
    } = props;
    const [mode, setMode] = useState<RichTextEditorProps["mode"]>(propsMode);
    const initialContent = initialValue ?? "";
    const latestContentRef = useRef(initialContent);
    const editorSeedRef = useRef(initialContent);
    const previousInitialValueRef = useRef(initialContent);
    const [editorSeed, setEditorSeed] = useState(editorSeedRef.current);

    const { t } = useTranslation();
    const border = useBorder();
    const spacing = useSpacing();
    const cornerRadius = useCornerRadius();
    const typography = useTypography();

    useEffect(() => {
      if (initialContent === previousInitialValueRef.current) return;

      previousInitialValueRef.current = initialContent;
      if (initialContent === latestContentRef.current) return;

      latestContentRef.current = initialContent;
      editorSeedRef.current = initialContent;
      setEditorSeed(initialContent);
    }, [initialContent]);

    useEffect(() => {
      if (!enableRTEPopupZIndexFix) return;
      document.body.classList.add(CUSTOM_LINK_ENABLED_CLASS);

      return () => {
        document.body.classList.remove(CUSTOM_LINK_ENABLED_CLASS);
      };
    }, [enableRTEPopupZIndexFix]);

    const setEditorSeedToLatestContent = (): void => {
      editorSeedRef.current = latestContentRef.current;
      setEditorSeed(latestContentRef.current);
    };

    const changeMode = (nextMode: RichTextEditorProps["mode"]): void => {
      if (nextMode === "editable") {
        setEditorSeedToLatestContent();
      }
      setMode(nextMode);
    };

    const handleRichTextEditorChange: RichTextEditorProps["onChange"] = (
      value,
      validationError,
    ): void => {
      latestContentRef.current = value;
      onChange?.(value, validationError);
    };

    const toolbarButtons: RichTextToolbarButtons = {
      undo: true,
      redo: true,
      copy: true,
      cut: true,
      paste: true,
      attachment: true,
      h1: true,
      h2: true,
      h3: true,
      h4: true,
      h5: true,
      h6: true,
      bold: true,
      italic: true,
      strikethrough: true,
      horizontalRule: true,
      underline: true,
      unorderedList: true,
      orderedList: true,
      link: true,
      ...(hidePreviewEditToggler
        ? {}
        : {
            customButtonGroups: [
              [
                {
                  name: "modeToggler",
                  tooltipLabel: mode === "preview" ? "Edit" : "Preview",
                  icon: mode === "preview" ? <Edit /> : <Eye />,
                  onClick: () =>
                    changeMode(mode === "preview" ? "editable" : "preview"),
                },
              ],
            ],
          }),
    };

    if (mode === "preview") {
      return (
        <Stack gap={spacing.spaceXS}>
          {!hidePreviewEditToggler && (
            <>
              <Typography {...typography.sh4}>
                {t("emailTemplates.contentLabel")} *
              </Typography>
              <Tooltip title="edit">
                <div style={{ width: "fit-content" }}>
                  <IconButton
                    name="editContent"
                    size="small"
                    icon={<Edit2 />}
                    variant="plain-neutral"
                    onClick={() => changeMode("editable")}
                  />
                </div>
              </Tooltip>
            </>
          )}
          <div
            style={{
              border: border.default,
              paddingInline: spacing.spaceMD,
              borderRadius: cornerRadius.radiusXS,
            }}
            dangerouslySetInnerHTML={{
              __html: decodeSavedHtml(latestContentRef.current),
            }}
          />
        </Stack>
      );
    }

    return (
      <RichTextEditor
        name="message"
        label={t("emailTemplates.contentLabel")}
        placeholder={t("emailTemplates.contentPlaceholder")}
        typography={richTextTypography()}
        valueType="html"
        supports={toolbarButtons}
        toolbarButtons={toolbarButtons}
        minHeight={MESSAGE_FIELD_HEIGHT}
        maxHeight={MESSAGE_FIELD_HEIGHT}
        attachmentConfig={rteFileService.richTextAttachmentProps}
        helperText={
          rteFileService.isRTEFileServiceError
            ? t("errors.rteFileServiceErrorHelperText")
            : helperText
        }
        error={rteFileService.isRTEFileServiceError || error}
        required
        {...richTextEditorProps}
        initialValue={editorSeed}
        onChange={handleRichTextEditorChange}
        mode={mode}
      />
    );
  });
